import type { PaymentIntentSnapshot } from '../interfaces/paymentIntentSnapshot.interface';
import type { CardPaymentIntentOptions } from '../interfaces/cardPayment.interface';
import { usePaymentInit } from './usePaymentInit';
import { usePaymentIntentCache } from './usePaymentIntentCache';
import { PAYMENT_INIT_ACTION } from '../common/constants/paymentInit';
import { isPaymentIntentUsable } from '../utils/paymentIntent';
import { isPaymentIntentSnapshotValid } from '../utils/paymentIntentSnapshot';
import { PaymentContractError } from '../utils/paymentContractError';

export function useCardPaymentIntent(options: CardPaymentIntentOptions) {
  const API = useKegelApi();
  const { isLago } = options;

  const paymentIntent = ref();
  const snapshot = ref<PaymentIntentSnapshot | null>(null);
  const intentCache = usePaymentIntentCache('card');

  function currentSnapshot(): PaymentIntentSnapshot {
    return {
      amount: options.roundedPrice.value,
      planCode: options.chargebeeId.value,
    };
  }

  const { load, isReady: isLoaded } = usePaymentInit({
    action: PAYMENT_INIT_ACTION.CARD_INTENT,
    requestKey: () => String(options.roundedPrice.value),
    initialize: async () => {
      if (isLago.value) return;

      const amount = options.roundedPrice.value;
      const intent = await intentCache.ensure(amount, async () => {
        const response = await API.creditCardInit(amount);

        if (!response?.payment_intent) {
          throw new PaymentContractError(`Card payment intent is missing in the init response for ${amount}`);
        }

        return response.payment_intent;
      });

      paymentIntent.value = intent;
      snapshot.value = { amount, planCode: options.chargebeeId.value };
    },
  });

  function drop(): void {
    if (snapshot.value !== null) intentCache.drop(snapshot.value.amount);
    if (isLago.value) options.dropLagoIntent?.();

    paymentIntent.value = undefined;
    snapshot.value = null;
  }

  function isUsable(): boolean {
    const mounted = snapshot.value;

    if (!isPaymentIntentSnapshotValid(mounted, currentSnapshot())) return false;

    return isPaymentIntentUsable(
      { amount: mounted.amount, expiresAt: paymentIntent.value?.expires_at },
      options.roundedPrice.value,
      Date.now(),
    );
  }

  async function refresh(): Promise<void> {
    if (isLago.value) {
      const requestedSnapshot = currentSnapshot();
      const intent = await options.ensureLagoIntent?.(options.nameOnCard.value);

      if (intent) {
        paymentIntent.value = intent;
        snapshot.value = requestedSnapshot;
      }

      return;
    }

    if (isUsable()) return;

    await load();

    if (isUsable()) return;

    if (!isLoaded.value) {
      throw new Error(`Card payment intent could not be loaded for ${options.roundedPrice.value}`);
    }

    throw new PaymentContractError(`Card payment intent is unavailable for ${options.roundedPrice.value}`);
  }

  if (options.lagoPaymentIntent) {
    watch(options.lagoPaymentIntent, (intent) => {
      if (intent) {
        paymentIntent.value = intent;
        snapshot.value = currentSnapshot();
      }
    }, { immediate: true });
  }

  watch(
    () => [options.discountPrice.value, options.roundedPrice.value, options.chargebeeId.value],
    () => {
      void load();
    },
    { immediate: import.meta.client && !isLago.value },
  );

  return {
    paymentIntent,
    snapshot,
    currentSnapshot,
    drop,
    load,
    refresh,
  };
}
